import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Flag, Calendar, Plus, History, ChevronRight, Check, 
  AlertTriangle, Clock, Loader2, Edit2, Trash2, FileText,
  Upload, Link2, Wand2, ArrowRight, Download, Eye, X
} from 'lucide-react';

const MILESTONE_TYPES = [
  { value: 'site_handover', label: 'Site Handover', category: 'access' },
  { value: 'access_date', label: 'Access Date', category: 'access' },
  { value: 'works_completion', label: 'Works Completion', category: 'completion' },
  { value: 'practical_completion', label: 'Practical Completion', category: 'completion' },
  { value: 'final_completion', label: 'Final Completion', category: 'completion' },
  { value: 'sectional_completion', label: 'Sectional Completion', category: 'completion' },
  { value: 'phase_completion', label: 'Phase Completion', category: 'completion' },
  { value: 'area_completion', label: 'Area Completion', category: 'area' },
  { value: 'other', label: 'Other', category: 'other' }
];

const STATUS_COLORS = {
  achieved: 'bg-green-100 text-green-700 border-green-200',
  pending: 'bg-slate-100 text-slate-700 border-slate-200',
  at_risk: 'bg-amber-100 text-amber-700 border-amber-200',
  overdue: 'bg-red-100 text-red-700 border-red-200'
};

const DOCUMENT_TYPES = {
  programme: { label: 'Programme (New Baseline)', icon: FileText, required: true },
  eot_claim: { label: 'EOT Claim PDF', icon: FileText, required: true },
  adjudication: { label: 'Adjudication', icon: FileText, required: true },
  supporting: { label: 'Supporting Documents', icon: FileText, required: false }
};

export default function MilestoneDatesModule({ projectId }) {
  const [milestones, setMilestones] = useState([]);
  const [summary, setSummary] = useState({});
  const [maxRevisions, setMaxRevisions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [programmes, setProgrammes] = useState([]);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [bulkAmendDialogOpen, setBulkAmendDialogOpen] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    milestone_type: 'practical_completion',
    name: '',
    description: '',
    area: '',
    baseline_date: ''
  });
  
  // Amend form with required documents
  const [amendForm, setAmendForm] = useState({
    new_date: '',
    days_changed: 0,
    reason: '',
    linked_claim_id: '',
    linked_claim_reference: '',
    linked_programme_id: '',
    programme_document: null,
    eot_claim_document: null,
    adjudication_document: null,
    supporting_documents: []
  });
  
  // Bulk amend form
  const [bulkAmendForm, setBulkAmendForm] = useState({
    milestone_revisions: [],
    reason: '',
    linked_claim_id: '',
    linked_claim_reference: '',
    linked_programme_id: '',
    programme_document: null,
    eot_claim_document: null,
    adjudication_document: null,
    supporting_documents: []
  });
  
  // File inputs refs
  const programmeInputRef = useRef(null);
  const eotClaimInputRef = useRef(null);
  const adjudicationInputRef = useRef(null);
  const supportingInputRef = useRef(null);

  useEffect(() => {
    loadMilestones();
    loadProgrammes();
  }, [projectId]);

  const loadMilestones = async () => {
    try {
      const response = await api.get(`/milestones/v2/project/${projectId}`);
      setMilestones(response.data.milestones || []);
      setSummary(response.data.summary || {});
      setMaxRevisions(response.data.max_revisions || 0);
    } catch (error) {
      console.error('Failed to load milestones:', error);
      // Fallback to legacy endpoint
      try {
        const legacyResponse = await api.get(`/milestones/project/${projectId}`);
        setMilestones(legacyResponse.data.milestones || []);
        setSummary(legacyResponse.data.summary || {});
      } catch (e) {
        console.error('Failed to load milestones (legacy):', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProgrammes = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/programs`);
      setProgrammes(response.data.programs || []);
    } catch (error) {
      console.error('Failed to load programmes:', error);
    }
  };

  const handleCreate = async () => {
    if (!createForm.name || !createForm.baseline_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/milestones/v2/', {
        project_id: projectId,
        ...createForm
      });
      toast.success('Milestone created');
      setCreateDialogOpen(false);
      setCreateForm({
        milestone_type: 'practical_completion',
        name: '',
        description: '',
        area: '',
        baseline_date: ''
      });
      loadMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create milestone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', docType);
    
    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return {
        file_name: file.name,
        file_url: response.data.file_url,
        file_size: file.size
      };
    } catch (error) {
      toast.error(`Failed to upload ${docType} document`);
      return null;
    }
  };

  const handleAmend = async () => {
    if (!amendForm.new_date || !amendForm.reason) {
      toast.error('Please fill in required fields');
      return;
    }
    
    // Check required documents
    if (!amendForm.programme_document && !amendForm.linked_programme_id) {
      toast.error('Please attach a programme document or link to an existing programme');
      return;
    }
    if (!amendForm.eot_claim_document) {
      toast.error('Please attach the EOT Claim PDF');
      return;
    }
    if (!amendForm.adjudication_document) {
      toast.error('Please attach the Adjudication document');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        new_date: amendForm.new_date,
        days_changed: amendForm.days_changed,
        reason: amendForm.reason,
        linked_claim_id: amendForm.linked_claim_id || null,
        linked_claim_reference: amendForm.linked_claim_reference || null,
        linked_programme_id: amendForm.linked_programme_id || null,
        programme_document: amendForm.programme_document,
        eot_claim_document: amendForm.eot_claim_document,
        adjudication_document: amendForm.adjudication_document,
        supporting_documents: amendForm.supporting_documents
      };
      
      await api.post(`/milestones/v2/${selectedMilestone.id}/revisions`, payload);
      toast.success('Milestone date amended successfully');
      setAmendDialogOpen(false);
      resetAmendForm();
      loadMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to amend milestone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkAmend = async () => {
    if (bulkAmendForm.milestone_revisions.length === 0) {
      toast.error('Please select at least one milestone to amend');
      return;
    }
    if (!bulkAmendForm.reason) {
      toast.error('Please provide a reason for the amendment');
      return;
    }
    
    // Check required documents
    if (!bulkAmendForm.programme_document && !bulkAmendForm.linked_programme_id) {
      toast.error('Please attach a programme document or link to an existing programme');
      return;
    }
    if (!bulkAmendForm.eot_claim_document) {
      toast.error('Please attach the EOT Claim PDF');
      return;
    }
    if (!bulkAmendForm.adjudication_document) {
      toast.error('Please attach the Adjudication document');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/milestones/v2/bulk-revision', bulkAmendForm);
      toast.success('Bulk amendment completed');
      setBulkAmendDialogOpen(false);
      resetBulkAmendForm();
      loadMilestones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete bulk amendment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAmendForm = () => {
    setAmendForm({
      new_date: '',
      days_changed: 0,
      reason: '',
      linked_claim_id: '',
      linked_claim_reference: '',
      linked_programme_id: '',
      programme_document: null,
      eot_claim_document: null,
      adjudication_document: null,
      supporting_documents: []
    });
  };

  const resetBulkAmendForm = () => {
    setBulkAmendForm({
      milestone_revisions: [],
      reason: '',
      linked_claim_id: '',
      linked_claim_reference: '',
      linked_programme_id: '',
      programme_document: null,
      eot_claim_document: null,
      adjudication_document: null,
      supporting_documents: []
    });
  };

  const openAmendDialog = (milestone) => {
    setSelectedMilestone(milestone);
    setAmendForm({
      new_date: milestone.current_date,
      days_changed: 0,
      reason: '',
      linked_claim_id: '',
      linked_claim_reference: '',
      linked_programme_id: '',
      programme_document: null,
      eot_claim_document: null,
      adjudication_document: null,
      supporting_documents: []
    });
    setAmendDialogOpen(true);
  };

  const openHistoryDialog = (milestone) => {
    setSelectedMilestone(milestone);
    setHistoryDialogOpen(true);
  };

  const markAchieved = async (milestone) => {
    const actualDate = prompt('Enter actual completion date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!actualDate) return;
    
    try {
      await api.post(`/milestones/v2/${milestone.id}/mark-achieved?actual_date=${actualDate}`);
      toast.success('Milestone marked as achieved');
      loadMilestones();
    } catch (error) {
      toast.error('Failed to mark milestone as achieved');
    }
  };

  const handleExtractMilestones = async (programmeId) => {
    setSubmitting(true);
    try {
      const response = await api.post(`/milestones/v2/extract-from-programme/${programmeId}`);
      const extracted = response.data.extracted_milestones || [];
      
      if (extracted.length === 0) {
        toast.info('No milestones could be extracted from the programme');
        return;
      }
      
      // Show extracted milestones and allow import
      const confirmed = window.confirm(`Found ${extracted.length} milestones. Import them?`);
      if (confirmed) {
        await api.post(`/milestones/v2/import-from-extraction?project_id=${projectId}`, {
          extracted_milestones: extracted,
          programme_id: programmeId
        });
        toast.success(`Imported ${extracted.length} milestones`);
        loadMilestones();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract milestones');
    } finally {
      setSubmitting(false);
      setExtractDialogOpen(false);
    }
  };

  // Generate revision columns header
  const getRevisionColumns = () => {
    const columns = [];
    for (let i = 1; i <= maxRevisions; i++) {
      columns.push(`Rev ${i}`);
    }
    return columns;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <Flag className="h-5 w-5" />
          Milestone Dates
          {summary.total_days_extended > 0 && (
            <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300">
              +{summary.total_days_extended} days extended
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {milestones.length > 1 && (
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkAmendForm({
                  ...bulkAmendForm,
                  milestone_revisions: milestones
                    .filter(m => m.status !== 'achieved')
                    .map(m => ({ milestone_id: m.id, new_date: m.current_date, days_changed: 0 }))
                });
                setBulkAmendDialogOpen(true);
              }}
              data-testid="bulk-amend-btn"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Bulk Amend
            </Button>
          )}
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setExtractDialogOpen(true)}
            data-testid="extract-milestones-btn"
          >
            <Wand2 className="h-4 w-4 mr-1" />
            Extract from Programme
          </Button>
          <Button 
            size="sm" 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-milestone-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Milestone
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Summary Stats */}
        {milestones.length > 0 && (
          <div className="grid grid-cols-5 gap-3 p-4 bg-slate-50 border-b">
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-2xl font-bold text-slate-900">{summary.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-2xl font-bold text-green-700">{summary.achieved}</p>
              <p className="text-xs text-green-600">Achieved</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-2xl font-bold text-slate-600">{summary.pending}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-2xl font-bold text-amber-700">{summary.at_risk}</p>
              <p className="text-xs text-amber-600">At Risk</p>
            </div>
            <div className="text-center p-2 bg-white rounded border">
              <p className="text-2xl font-bold text-red-700">{summary.overdue}</p>
              <p className="text-xs text-red-600">Overdue</p>
            </div>
          </div>
        )}

        {/* Milestones Table */}
        {milestones.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Flag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No milestones defined yet</p>
            <p className="text-sm mt-1">Add key project milestones to track completion dates</p>
            <Button 
              className="mt-4"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Milestone
            </Button>
          </div>
        ) : (
          <ScrollArea className="w-full">
            <div className="min-w-[800px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold sticky left-0 bg-slate-100 z-10 min-w-[200px]">Milestone</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">Baseline Date</th>
                    <th className="text-center p-3 font-semibold min-w-[100px]">Working Days Left</th>
                    {getRevisionColumns().map((col, idx) => (
                      <th key={idx} className="text-center p-3 font-semibold min-w-[120px] bg-blue-50">
                        {col}
                      </th>
                    ))}
                    <th className="text-center p-3 font-semibold min-w-[120px]">Current Date</th>
                    <th className="text-center p-3 font-semibold min-w-[100px]">Status</th>
                    <th className="text-center p-3 font-semibold min-w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((milestone, idx) => (
                    <tr key={milestone.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      {/* Milestone Name */}
                      <td className="p-3 sticky left-0 bg-inherit z-10 border-r">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium">{milestone.name}</p>
                            <p className="text-xs text-slate-500">
                              {MILESTONE_TYPES.find(t => t.value === milestone.milestone_type)?.label}
                              {milestone.area && ` • ${milestone.area}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      
                      {/* Baseline Date */}
                      <td className="p-3 text-center font-medium border-r bg-slate-50">
                        {milestone.baseline_date || milestone.original_date}
                      </td>
                      
                      {/* Working Days Left */}
                      <td className="p-3 text-center border-r">
                        {milestone.status === 'achieved' ? (
                          <span className="text-green-600">-</span>
                        ) : (
                          <span className={milestone.working_days_left <= 14 ? 'text-amber-600 font-medium' : ''}>
                            {milestone.working_days_left}
                          </span>
                        )}
                      </td>
                      
                      {/* Revision Columns */}
                      {Array.from({ length: maxRevisions }).map((_, revIdx) => {
                        const revision = milestone.revisions?.[revIdx];
                        return (
                          <td key={revIdx} className="p-3 text-center border-r bg-blue-50/30">
                            {revision ? (
                              <div className="space-y-1">
                                <p className="font-medium">{revision.new_date}</p>
                                <p className="text-xs text-slate-500">
                                  {revision.days_changed > 0 ? '+' : ''}{revision.days_changed} days
                                </p>
                                {revision.documents?.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {revision.documents.length} docs
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                        );
                      })}
                      
                      {/* Current Date */}
                      <td className="p-3 text-center border-r">
                        <span className={`font-medium ${
                          milestone.current_date !== (milestone.baseline_date || milestone.original_date) 
                            ? 'text-amber-600' 
                            : ''
                        }`}>
                          {milestone.current_date}
                        </span>
                        {milestone.actual_date && (
                          <p className="text-xs text-green-600 mt-1">
                            Actual: {milestone.actual_date}
                          </p>
                        )}
                      </td>
                      
                      {/* Status */}
                      <td className="p-3 text-center border-r">
                        <Badge className={STATUS_COLORS[milestone.status]}>
                          {milestone.status === 'at_risk' ? 'At Risk' : milestone.status}
                        </Badge>
                      </td>
                      
                      {/* Actions */}
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {milestone.status !== 'achieved' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openAmendDialog(milestone)}
                                title="Amend Baseline"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => markAchieved(milestone)}
                                className="text-green-600 hover:text-green-700"
                                title="Mark as Achieved"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {milestone.revisions?.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openHistoryDialog(milestone)}
                              title="View History"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </CardContent>

      {/* Create Milestone Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>
              Define a key project milestone date to track.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Milestone Type *</Label>
              <Select 
                value={createForm.milestone_type} 
                onValueChange={(v) => setCreateForm(prev => ({ ...prev, milestone_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MILESTONE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input 
                className="mt-1"
                placeholder="e.g., Practical Completion - Block A"
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Area (Optional)</Label>
              <Input 
                className="mt-1"
                placeholder="e.g., Block A, Phase 1"
                value={createForm.area}
                onChange={(e) => setCreateForm(prev => ({ ...prev, area: e.target.value }))}
              />
            </div>
            <div>
              <Label>Baseline Date *</Label>
              <Input 
                type="date"
                className="mt-1"
                value={createForm.baseline_date}
                onChange={(e) => setCreateForm(prev => ({ ...prev, baseline_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea 
                className="mt-1"
                placeholder="Additional details..."
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amend Milestone Dialog */}
      <Dialog open={amendDialogOpen} onOpenChange={setAmendDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Amend Baseline Date</DialogTitle>
            <DialogDescription>
              Amending a milestone requires uploading supporting documentation.
            </DialogDescription>
          </DialogHeader>
          {selectedMilestone && (
            <div className="space-y-4 py-4">
              {/* Current Milestone Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedMilestone.name}</p>
                <p className="text-sm text-slate-500">
                  Current Date: {selectedMilestone.current_date} | 
                  Baseline: {selectedMilestone.baseline_date || selectedMilestone.original_date}
                </p>
              </div>
              
              {/* New Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>New Date *</Label>
                  <Input 
                    type="date"
                    className="mt-1"
                    value={amendForm.new_date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      const currentDate = new Date(selectedMilestone.current_date);
                      const newDateObj = new Date(newDate);
                      const daysDiff = Math.round((newDateObj - currentDate) / (1000 * 60 * 60 * 24));
                      setAmendForm(prev => ({ ...prev, new_date: newDate, days_changed: daysDiff }));
                    }}
                  />
                </div>
                <div>
                  <Label>Days Changed</Label>
                  <Input 
                    type="number"
                    className="mt-1"
                    value={amendForm.days_changed}
                    onChange={(e) => setAmendForm(prev => ({ ...prev, days_changed: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              
              {/* Reason */}
              <div>
                <Label>Reason for Amendment *</Label>
                <Textarea 
                  className="mt-1"
                  placeholder="e.g., Approved EOT Claim #123 for adverse weather"
                  value={amendForm.reason}
                  onChange={(e) => setAmendForm(prev => ({ ...prev, reason: e.target.value }))}
                />
              </div>
              
              {/* Linked Claim Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Claim Reference</Label>
                  <Input 
                    className="mt-1"
                    placeholder="e.g., EOT-2026-001"
                    value={amendForm.linked_claim_reference}
                    onChange={(e) => setAmendForm(prev => ({ ...prev, linked_claim_reference: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Link to Programme</Label>
                  <Select 
                    value={amendForm.linked_programme_id} 
                    onValueChange={(v) => setAmendForm(prev => ({ ...prev, linked_programme_id: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select existing programme..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None (Upload new)</SelectItem>
                      {programmes.map(prog => (
                        <SelectItem key={prog.id} value={prog.id}>
                          {prog.title} (v{prog.version})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Required Documents Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Required Documents
                </h4>
                <p className="text-sm text-slate-500 mb-4">
                  All documents below must be uploaded for the revision to be processed.
                </p>
                
                <div className="space-y-3">
                  {/* Programme Document */}
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">Programme (New Baseline) *</p>
                        {amendForm.programme_document ? (
                          <p className="text-xs text-green-600">{amendForm.programme_document.file_name}</p>
                        ) : amendForm.linked_programme_id ? (
                          <p className="text-xs text-blue-600">Linked to existing programme</p>
                        ) : (
                          <p className="text-xs text-slate-400">Not uploaded</p>
                        )}
                      </div>
                    </div>
                    {!amendForm.linked_programme_id && (
                      <div>
                        <input
                          ref={programmeInputRef}
                          type="file"
                          accept=".pdf,.mpp,.xer"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const uploaded = await handleFileUpload(file, 'programme');
                              if (uploaded) {
                                setAmendForm(prev => ({ ...prev, programme_document: uploaded }));
                              }
                            }
                          }}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => programmeInputRef.current?.click()}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* EOT Claim Document */}
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">EOT Claim PDF *</p>
                        {amendForm.eot_claim_document ? (
                          <p className="text-xs text-green-600">{amendForm.eot_claim_document.file_name}</p>
                        ) : (
                          <p className="text-xs text-slate-400">Not uploaded</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <input
                        ref={eotClaimInputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const uploaded = await handleFileUpload(file, 'eot_claim');
                            if (uploaded) {
                              setAmendForm(prev => ({ ...prev, eot_claim_document: uploaded }));
                            }
                          }
                        }}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => eotClaimInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </div>
                  
                  {/* Adjudication Document */}
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">Adjudication of Claim *</p>
                        {amendForm.adjudication_document ? (
                          <p className="text-xs text-green-600">{amendForm.adjudication_document.file_name}</p>
                        ) : (
                          <p className="text-xs text-slate-400">Not uploaded</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <input
                        ref={adjudicationInputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const uploaded = await handleFileUpload(file, 'adjudication');
                            if (uploaded) {
                              setAmendForm(prev => ({ ...prev, adjudication_document: uploaded }));
                            }
                          }
                        }}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => adjudicationInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </div>
                  
                  {/* Supporting Documents */}
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">Supporting Documents (Optional)</p>
                          <p className="text-xs text-slate-400">
                            {amendForm.supporting_documents.length} file(s) uploaded
                          </p>
                        </div>
                      </div>
                      <div>
                        <input
                          ref={supportingInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const file of files) {
                              const uploaded = await handleFileUpload(file, 'supporting');
                              if (uploaded) {
                                setAmendForm(prev => ({ 
                                  ...prev, 
                                  supporting_documents: [...prev.supporting_documents, uploaded] 
                                }));
                              }
                            }
                          }}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => supportingInputRef.current?.click()}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                    {amendForm.supporting_documents.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {amendForm.supporting_documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                            <span>{doc.file_name}</span>
                            <button 
                              onClick={() => setAmendForm(prev => ({
                                ...prev,
                                supporting_documents: prev.supporting_documents.filter((_, i) => i !== idx)
                              }))}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAmend} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Amend Baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Revision History</DialogTitle>
          </DialogHeader>
          {selectedMilestone && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-slate-50 rounded-lg mb-4">
                <p className="font-medium">{selectedMilestone.name}</p>
                <p className="text-sm text-slate-500">
                  Baseline: {selectedMilestone.baseline_date || selectedMilestone.original_date} → 
                  Current: {selectedMilestone.current_date}
                </p>
              </div>
              
              {selectedMilestone.revisions?.map((rev, idx) => (
                <div key={rev.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge>Revision {rev.revision_number || idx + 1}</Badge>
                    <span className="text-xs text-slate-500">
                      {new Date(rev.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-slate-500">Previous Date</p>
                      <p className="font-medium">{rev.previous_date}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">New Date</p>
                      <p className="font-medium">{rev.new_date}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Days Changed</p>
                      <p className="font-medium text-amber-600">
                        {rev.days_changed > 0 ? '+' : ''}{rev.days_changed}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm mb-3">
                    <p className="text-slate-500">Reason</p>
                    <p>{rev.reason}</p>
                  </div>
                  {rev.linked_claim_reference && (
                    <div className="text-sm mb-3">
                      <p className="text-slate-500">Claim Reference</p>
                      <p>{rev.linked_claim_reference}</p>
                    </div>
                  )}
                  {rev.documents?.length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-slate-500 mb-2">Attached Documents ({rev.documents.length})</p>
                      <div className="space-y-1">
                        {rev.documents.map((doc, docIdx) => (
                          <div key={docIdx} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <FileText className="h-3 w-3" />
                              <span>{doc.file_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {DOCUMENT_TYPES[doc.document_type]?.label || doc.document_type}
                              </Badge>
                            </div>
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                              <Download className="h-3 w-3 text-blue-600" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    By: {rev.created_by_name || 'Unknown'}
                  </p>
                </div>
              ))}
              
              {(!selectedMilestone.revisions || selectedMilestone.revisions.length === 0) && (
                <p className="text-center text-slate-500 py-8">No revisions recorded</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extract from Programme Dialog */}
      <Dialog open={extractDialogOpen} onOpenChange={setExtractDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Extract Milestones from Programme
            </DialogTitle>
            <DialogDescription>
              Use AI to automatically extract milestone dates from an uploaded programme PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Programme</Label>
            <div className="space-y-2 mt-2">
              {programmes.length === 0 ? (
                <p className="text-sm text-slate-500">No programmes uploaded yet.</p>
              ) : (
                programmes.map(prog => (
                  <div 
                    key={prog.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                    onClick={() => handleExtractMilestones(prog.id)}
                  >
                    <div>
                      <p className="font-medium">{prog.title}</p>
                      <p className="text-xs text-slate-500">Version {prog.version} • {prog.status}</p>
                    </div>
                    <Button variant="ghost" size="sm" disabled={submitting}>
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Amend Dialog - Similar structure to Amend Dialog but for multiple milestones */}
      <Dialog open={bulkAmendDialogOpen} onOpenChange={setBulkAmendDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Amend Milestones</DialogTitle>
            <DialogDescription>
              Amend multiple milestone dates at once (e.g., after an approved EOT).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Milestones to amend */}
            <div>
              <Label className="mb-2 block">Milestones to Amend</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {bulkAmendForm.milestone_revisions.map((rev, idx) => {
                  const milestone = milestones.find(m => m.id === rev.milestone_id);
                  if (!milestone) return null;
                  return (
                    <div key={rev.milestone_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{milestone.name}</p>
                        <p className="text-xs text-slate-500">Current: {milestone.current_date}</p>
                      </div>
                      <Input 
                        type="date"
                        className="w-40"
                        value={rev.new_date}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          const currentDate = new Date(milestone.current_date);
                          const newDateObj = new Date(newDate);
                          const daysDiff = Math.round((newDateObj - currentDate) / (1000 * 60 * 60 * 24));
                          setBulkAmendForm(prev => ({
                            ...prev,
                            milestone_revisions: prev.milestone_revisions.map((r, i) =>
                              i === idx ? { ...r, new_date: newDate, days_changed: daysDiff } : r
                            )
                          }));
                        }}
                      />
                      <span className="text-xs w-16 text-right">
                        {rev.days_changed > 0 ? '+' : ''}{rev.days_changed} days
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Common fields */}
            <div>
              <Label>Reason for Amendment *</Label>
              <Textarea 
                className="mt-1"
                placeholder="e.g., Approved EOT Claim #123"
                value={bulkAmendForm.reason}
                onChange={(e) => setBulkAmendForm(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>
            
            {/* Same document upload section as single amend */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Required Documents (shared for all milestones)</h4>
              <p className="text-sm text-slate-500 mb-3">
                Upload the programme, EOT claim, and adjudication once - they will be linked to all selected milestones.
              </p>
              {/* Simplified document indicators */}
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className={`p-2 border rounded text-center ${bulkAmendForm.programme_document || bulkAmendForm.linked_programme_id ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  Programme {bulkAmendForm.programme_document || bulkAmendForm.linked_programme_id ? '✓' : '✗'}
                </div>
                <div className={`p-2 border rounded text-center ${bulkAmendForm.eot_claim_document ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  EOT Claim {bulkAmendForm.eot_claim_document ? '✓' : '✗'}
                </div>
                <div className={`p-2 border rounded text-center ${bulkAmendForm.adjudication_document ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  Adjudication {bulkAmendForm.adjudication_document ? '✓' : '✗'}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Please use the single "Amend Baseline" button on any milestone to upload the required documents.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAmendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAmend} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Apply Bulk Amendment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
